import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbCategory } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';

interface Category {
  id: number;
  name: string;
  description?: string;
  color: string;
  transactionCount: number;
}

interface CategoriesPageProps {
  hideHeader?: boolean;
}

export default function CategoriesPage({ hideHeader = false }: CategoriesPageProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.categories' });

  // Mock data for categories
  const [categories, setCategories] = useState<Category[]>([
    {
      id: 1,
      name: 'Office Supplies',
      description: 'Stationery, equipment, and office materials',
      color: '#3B82F6',
      transactionCount: 12,
    },
    {
      id: 2,
      name: 'Travel & Transport',
      description: 'Business travel, fuel, and transportation costs',
      color: '#10B981',
      transactionCount: 8,
    },
    {
      id: 3,
      name: 'Marketing',
      description: 'Advertising, promotional materials, and marketing campaigns',
      color: '#F59E0B',
      transactionCount: 5,
    },
    {
      id: 4,
      name: 'Utilities',
      description: 'Electricity, water, internet, and other utilities',
      color: '#EF4444',
      transactionCount: 15,
    },
    {
      id: 5,
      name: 'Professional Services',
      description: 'Legal, accounting, consulting, and other professional services',
      color: '#8B5CF6',
      transactionCount: 3,
    },
  ]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
  });

  const colorOptions = [
    { label: 'Blue', value: '#3B82F6' },
    { label: 'Green', value: '#10B981' },
    { label: 'Yellow', value: '#F59E0B' },
    { label: 'Red', value: '#EF4444' },
    { label: 'Purple', value: '#8B5CF6' },
    { label: 'Pink', value: '#EC4899' },
    { label: 'Indigo', value: '#6366F1' },
    { label: 'Teal', value: '#14B8A6' },
  ];

  const handleCreateCategory = () => {
    if (!newCategory.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    const category: Category = {
      id: Math.max(...categories.map((c) => c.id)) + 1,
      name: newCategory.name.trim(),
      description: newCategory.description.trim() || undefined,
      color: newCategory.color,
      transactionCount: 0,
    };

    setCategories((prev) => [...prev, category]);
    setNewCategory({ name: '', description: '', color: '#3B82F6' });
    setIsCreateModalOpen(false);
    toast.success(t('success.created'));
  };

  const handleEditCategory = () => {
    if (!editingCategory || !editingCategory.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === editingCategory.id
          ? {
              ...cat,
              name: editingCategory.name.trim(),
              description: editingCategory.description?.trim() || undefined,
              color: editingCategory.color,
            }
          : cat,
      ),
    );

    setEditingCategory(null);
    setIsEditModalOpen(false);
    toast.success(t('success.updated'));
  };

  const handleDeleteCategory = (category: Category) => {
    if (category.transactionCount > 0) {
      toast.error(t('errors.cannotDeleteWithTransactions'));
      return;
    }

    if (window.confirm(t('confirmations.delete', { name: category.name }))) {
      setCategories((prev) => prev.filter((cat) => cat.id !== category.id));
      toast.success(t('success.deleted'));
    }
  };

  const openEditModal = (category: Category) => {
    setEditingCategory({ ...category });
    setIsEditModalOpen(true);
  };

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
              <TbPlus />
              {t('addCategory')}
            </Button>
          }
        />
      )}

      <div className="space-y-6">
        {/* Categories Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <div
              key={category.id}
              className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ backgroundColor: category.color }}>
                    <TbCategory className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                    <p className="text-sm text-gray-500">
                      {category.transactionCount} transactions
                    </p>
                  </div>
                </div>
                <ActionMenu>
                  <ActionMenuButton
                    icon={TbEdit}
                    text={t('edit')}
                    onClick={() => openEditModal(category)}
                  />
                  <ActionMenuButton
                    icon={TbTrash}
                    text={t('delete')}
                    onClick={() => handleDeleteCategory(category)}
                    className="text-danger hover:text-red-700"
                  />
                </ActionMenu>
              </div>
              {category.description && (
                <p className="mb-4 text-sm text-gray-600">{category.description}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="h-4 w-4 rounded-full" style={{ backgroundColor: category.color }} />
                <span className="text-xs text-gray-500">
                  {category.transactionCount === 0 ? t('noTransactions') : t('hasTransactions')}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {categories.length === 0 && (
          <div className="py-12 text-center">
            <TbCategory className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">{t('emptyState.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('emptyState.description')}</p>
            <div className="mt-4">
              <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
                <TbPlus />
                {t('addFirstCategory')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Category Modal */}
      <ModalLayout
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('createModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="category-name"
              label={t('createModal.name')}
              type="text"
              placeholder={t('createModal.namePlaceholder')}
              value={newCategory.name}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Labeled label={t('createModal.description')}>
              <textarea
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('createModal.descriptionPlaceholder')}
                value={newCategory.description}
                onChange={(e) =>
                  setNewCategory((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </Labeled>
            <Labeled label={t('createModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      newCategory.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setNewCategory((prev) => ({ ...prev, color: color.value }))}
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleCreateCategory}>
                {t('create')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Edit Category Modal */}
      <ModalLayout
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('editModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="edit-category-name"
              label={t('editModal.name')}
              type="text"
              placeholder={t('editModal.namePlaceholder')}
              value={editingCategory?.name || ''}
              onChange={(e) =>
                setEditingCategory((prev) => (prev ? { ...prev, name: e.target.value } : null))
              }
            />
            <Labeled label={t('editModal.description')}>
              <textarea
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('editModal.descriptionPlaceholder')}
                value={editingCategory?.description || ''}
                onChange={(e) =>
                  setEditingCategory((prev) =>
                    prev ? { ...prev, description: e.target.value } : null,
                  )
                }
              />
            </Labeled>
            <Labeled label={t('editModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      editingCategory?.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() =>
                      setEditingCategory((prev) => (prev ? { ...prev, color: color.value } : null))
                    }
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleEditCategory}>
                {t('update')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}
